import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

export default function RecentDiscoveryCards({ ideas, palette, compact = false, sending, onOpen, onContinue }) {
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  if (!ideas.length) return null;
  const sendAction = idea => <Pressable accessibilityRole="button" accessibilityLabel={`Send and continue previous chat: ${idea.description}`}
    accessibilityHint={`Sends this message in that conversation: ${idea.prompt}`}
    disabled={Boolean(sending)} accessibilityState={{ disabled: Boolean(sending) }}
    onPress={() => onContinue?.(idea)} style={({ hovered, pressed }) => [compact ? styles.compactSend : styles.send,
      { backgroundColor: hovered ? palette.hover : compact ? palette.preview : 'transparent', opacity: pressed ? 0.7 : 1 }]}>
    {!compact && <Text size="s" weight="semibold">{sending === idea.id ? 'Opening previous chat…' : 'Send & continue chat'}</Text>}
    <Icon name={compact ? (sending === idea.id ? 'ellipsis' : 'paper-plane') : 'arrow-right'} size={compact ? 16 : 13} color={palette.icon} />
  </Pressable>;
  return <View style={[styles.section, compact && styles.compactSection]} testID="recent-discovery">
    <Text size="s" weight="medium" color="secondary">{compact ? 'Recent chat' : 'Recent chats'}</Text>
    <View style={[styles.cards, compact && styles.compactCards]}>{ideas.map(idea => <View key={idea.id} style={[styles.card, compact && styles.compactCard, { borderColor: palette.border, backgroundColor: palette.surface }]}>
      <Pressable accessibilityRole="link" accessibilityLabel={`Open previous chat: ${idea.description}`}
        accessibilityHint="Opens the existing conversation without sending a message."
        onPress={() => onOpen?.(idea)}
        style={({ hovered, pressed }) => [styles.chat, compact && styles.compactChat, { opacity: pressed ? 0.7 : 1, backgroundColor: hovered ? palette.hover : 'transparent' }]}>
        <View style={[styles.chatIcon, compact && styles.compactIcon, { backgroundColor: palette.preview }]}><Icon name="comments" size={17} color={palette.icon} /></View>
        <View style={styles.chatTitle}>
          {!compact && <Text size="xxs" color="secondary">Previous chat</Text>}
          <Text size="s" weight="medium" numberOfLines={compact ? 1 : 2}>{idea.description}</Text>
        </View>
        <Icon name="chevron-right" size={10} color={palette.icon} />
      </Pressable>
      {!idea.prompt ? <View style={[styles.message, styles.pendingMessage, { backgroundColor: palette.preview }]}>
        <Text size="s" color="secondary">{idea.status === 'loading' ? 'Preparing a next message…' : 'Open this chat to pick up where you left off.'}</Text>
      </View> : compact ? <View style={styles.compactMessage}>
        <Pressable accessibilityRole="button" accessibilityLabel={`Read suggested next message: ${idea.prompt}`}
          accessibilityState={{ expanded: expandedSuggestion === idea.id }} aria-expanded={expandedSuggestion === idea.id}
          onPress={() => setExpandedSuggestion(value => value === idea.id ? null : idea.id)} style={styles.compactPrompt}>
          <Text size="s" color="secondary" numberOfLines={expandedSuggestion === idea.id ? undefined : 2}>{idea.prompt}</Text>
        </Pressable>
        {sendAction(idea)}
      </View> : <>
        <View style={[styles.message, { backgroundColor: palette.preview }]}>
          <Text size="xxs" color="secondary">Suggested next message</Text>
          <Text size="s" style={styles.prompt}>{idea.prompt}</Text>
        </View>
        {sendAction(idea)}
      </>}
    </View>)}</View>
  </View>;
}
const styles = StyleSheet.create({
  compactSection: { gap: 10, marginBottom: 18 },
  compactCards: { flexDirection: 'column', flexWrap: 'nowrap', gap: 8 },
  compactCard: { flexGrow: 0, flexBasis: 'auto', paddingHorizontal: 12, paddingVertical: 8, gap: 0, borderRadius: 16 },
  compactChat: { minHeight: 44, gap: 8 },
  compactIcon: { width: 28, height: 28, borderRadius: 9 },
  compactMessage: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  compactPrompt: { flex: 1, minWidth: 0, minHeight: 44, justifyContent: 'center', paddingVertical: 4 },
  compactSend: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  section: { gap: 16, marginBottom: 24 },
  cards: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  card: { flexGrow: 1, flexBasis: 260, minWidth: 0, borderWidth: 1, borderRadius: 20, padding: 16, gap: 16 },
  chat: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 52, borderRadius: 10 },
  chatIcon: { width: 36, height: 36, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  chatTitle: { flex: 1, minWidth: 0, gap: 4 },
  pendingMessage: { minHeight: 88, justifyContent: 'center' },
  message: { flexGrow: 1, borderRadius: 14, borderBottomRightRadius: 4, padding: 14, gap: 8 },
  prompt: { lineHeight: 22 },
  send: { minHeight: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingHorizontal: 4, borderRadius: 8 },
});
